"use client"

/**
 * React hook
 */
import { useEffect, useState } from "react"

/**
 * Next.js Link component
 */
import Link from "next/link"

/**
 * UI components
 */
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

/**
 * Icons
 */
import {
  Search,
  Plus,
  AlertCircle,
  Copy,
  Eye,
  Edit,
  Archive,
  ArchiveRestore,
  RefreshCw,
  Loader2
} from "lucide-react"

/**
 * UI helpers
 */
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { LoadingSpinner } from "@/components/ui/loading-spinner"

/**
 * Confirmation dialogs
 */
import { ActionConfirmDialog, ConfirmDialog } from "@/components/ui/confirm-dialog"

/**
 * Templates hook that handles data fetching and actions
 */
import { useTemplatesHook } from "@/hooks/useTemplatesHook"

/**
 * Table component for displaying templates
 */
import TemplateTable from "@/components/templateComponent/TemplateTable"
import { TagLoaders } from "@/components/TagLoaders"
import { apiFetch } from "@/lib/api/apiFetch"
import { useUser } from "@/hooks/useUser"

/**
 * TemplatesPage
 *
 * Main page responsible for:
 * - Listing email templates
 * - Searching templates via API
 * - Pagination handling
 * - Archiving / restoring templates
 * - Duplicating templates
 *
 * Route:
 * - /templates
 *
 * @returns JSX.Element
 */
export default function TemplatesPage() {

  /**
   * Template-related state and handlers
   * provided by custom templates hook
   */
  const {
    confirmArchiveTemplate,
    fetchTemplates,
    handleSearch,
    handleSearchChange,
    handleArchiveToggle,
    handleDuplicate,
    handlePageChange,
    searchTimeout,
    searchTerm,
    templates,
    loading,
    error,
    selectedCategory,
    setSearchTerm,
    setSelectedCategory,
    setCurrentPage,
    processingIds,
    handleArchiveTemplate,
    archivingTemplates,
    pagination,
    currentPage,
    showArchiveDialog,
    setShowArchiveDialog,
    templateToArchive
  } = useTemplatesHook()
  const { user } = useUser()
  const [btnLoader, setBtnLoader] = useState(false)

  /**
   * Initial fetch of templates list
   */
  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  /**
   * Cleanup search debounce timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    }
  }, [searchTimeout])

  /**
   * Templates to be displayed.
   * (Server-side search is used, so no client filtering)
   */
  const displayTemplates = templates

  /**
   * Initial loading state UI
   */
  if (loading && templates.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <TagLoaders
          tags={["Templates"]}
          message="Loading template library..."
          variant="ring"
        />

      </div>
    )
  }

  const goToEditor = async () => {
    try {
      setBtnLoader(true)
      const res = await apiFetch('/api/short-link', {
        method: 'POST',
        data: {
          metadata: {
            type: 'EMAIL_TEMP_EDITOR',
            userName: user?.name,
            userId: user?._id
          }
        }
      });

      const shortLink = res?.data?.shortUrl;

      if (shortLink) {
        window.location.href = shortLink;
      }

    } catch (error) {
      console.error("Failed to generate short link:", error);
    } finally {
      setBtnLoader(false)
    }
  };

  /**
   * Main templates list layout
   */
  return (
    <div className="px-4 animate-in fade-in zoom-in-90 duration-1000 ease-out">
      {/* Header */}
      {/* Action Toolbar */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">

        {/* Left Section - Search + Refresh */}
        <div className="flex items-center gap-3 w-full md:w-auto">

          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-10 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500"
            />
          </div>

          {/* Refresh */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchTemplates}
            disabled={loading}
            className="border-gray-200 dark:border-gray-700"
            title="Refresh"
          >
            <RefreshCw
              className={`h-4 w-4 transition-transform ${loading ? "animate-spin" : ""
                }`}
            />
          </Button>
        </div>

        {/* Right Section - Add Button */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <Button className="shadow-sm" disabled={btnLoader} onClick={goToEditor} >{btnLoader ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : "Create From Editor"}</Button>

          <Link href="/templates/new">
            <Button className="shadow-sm">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </Link>
        </div>
      </div>


      {/* Error alert */}
      {error && (
        <Alert className="mb-6" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Templates table */}
      <TemplateTable
        displayTemplates={displayTemplates}
        loading={loading}
        searchTerm={searchTerm}
        selectedCategory={selectedCategory}
        setSearchTerm={setSearchTerm}
        setSelectedCategory={setSelectedCategory}
        setCurrentPage={setCurrentPage}
        handleDuplicate={handleDuplicate}
        processingIds={processingIds}
        handleArchiveTemplate={handleArchiveTemplate}
        archivingTemplates={archivingTemplates}
        handleArchiveToggle={handleArchiveToggle}
      />

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} templates
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              const pageNum = i + 1

              if (pagination.pages <= 5) {
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                )
              }

              if (
                pageNum === 1 ||
                pageNum === pagination.pages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                )
              }

              if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return (
                  <span key={pageNum} className="text-sm text-gray-600">
                    ...
                  </span>
                )
              }

              return null
            })}

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Archive confirmation dialog */}
      <ActionConfirmDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        title="Archive Template"
        description={
          <>
            Are you sure you want to archive the template "{templateToArchive?.name}"?
            <br /><br />
            <strong>This will:</strong><br />
            • Move the template to the archive<br />
            • Hide it from the main template list<br />
            • Preserve all template data<br />
            • Allow you to restore it later if needed<br />
            <br />
            The template can be restored from the archive page at any time.
          </>
        }
        actionText="Archive template"
        onConfirm={confirmArchiveTemplate}
      />
    </div>
  )
}